<template>
  <v-card>
    <v-card-text>
      <v-form @submit.prevent="onSubmit">
        <v-row>
          <v-col>
            <v-text-field
              v-model="localEnergyPriceSet.description"
              :label="$tc('description')"
              :error-messages="descriptionErrors"/>
            <v-text-field
              v-model="localEnergyPriceSet.code"
              :label="$tc('code')"
              :error-messages='codeErrors'
              :disabled="localEnergyPriceSet.id > 0"/>
            <v-select
              v-model="localEnergyPriceSet.type"
              :label="$tc('type', 2)"
              :items="energyPriceSetTypes"
              :error-messages="typeErrors"
              item-text="text"
              item-value="value"/>
            <v-select
              v-model="localEnergyPriceSet.unit_of_measure"
              :label="$tc('unit_of_measure')"
              :items="unitsOfMeasure"
              :error-messages="unitOfMeasureErrors"
              item-text="text"
              item-value="value"/>
            <v-select
              v-model="localEnergyPriceSet.currency"
              :label="$tc('currency')"
              :items="currencies"
              :error-messages="currencyErrors"
              item-text="text"
              item-value="value"/>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="3">
            <label class="col-form-label text-sm-right">{{ $tc('price', 2) }}</label>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-row v-for="(priceHistoryItem, index) in localPriceHistory" :key="index" class="pt-1">
              <v-col cols="2">
                <date-picker v-model="priceHistoryItem.date" :options="datePickerOptions" class="cursor-pointer"
                             @input="sortLocalPriceHistory"/>
              </v-col>
              <v-col cols="2">
                <v-text-field v-model.number="priceHistoryItem.price" type="number" min="0" step=".01"/>
              </v-col>
              <v-col cols="2">
                <v-btn color="error" @click="deletePrice(priceHistoryItem.date)">
                  <v-icon >mdi-delete</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="6">
            <v-btn class="mb-3" color="success" @click="addPrice()">
              <v-icon>mdi-plus</v-icon>
              Add Price
            </v-btn>
          </v-col>
        </v-row>

        <vf-autocomplete
          v-model="selectedCustomers"
          :label="$tc('customer', 2)"
          :items="customers"
          multiple
          return-object
          item-text="name"
          item-value="id"/>

        <v-btn v-if="!localEnergyPriceSet.id"
          type="submit"
          color="primary"
          @click="$gtag.event('create', {event_category: 'EnergyPriceSet'})"
          :loading="isSaving">
          <v-icon class="ml-n2">mdi-content-save</v-icon>
          {{ $t('create') }}
        </v-btn>
        <v-btn v-else
               type="submit"
               color="primary"
               @click="$gtag.event('update', {event_category: 'EnergyPriceSet'})"
               :loading="isSaving">
          <v-icon class="ml-n2">mdi-content-save</v-icon>
          {{ $t('update') }}
        </v-btn>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import VfAutocomplete from '@/components/shared/VfAutocomplete'
import DatePicker from '@/components/shared/DatePicker'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import EnergyPriceSetRecord from '@/orm/models/EnergyPriceSetRecord'
import Moment from 'moment-timezone'
import { required } from 'vuelidate/lib/validators'
import { get, cloneDeep, map, differenceWith, forEach, filter } from 'lodash'

export default {
  name: 'EnergyPriceSetForm',
  components: {
    VfAutocomplete,
    DatePicker
  },
  mixins: [
    CurrentUserMixin,
    ValidationMixin
  ],
  props: {
    energyPriceSet: {
      type: Object
    }
  },
  validations: {
    localEnergyPriceSet: {
      description: {
        required
      },
      code: {
        required
      },
      type: {
        required
      },
      unit_of_measure: {
        required
      },
      currency: {
        required
      }
    }
  },
  computed: {
    descriptionErrors () {
      return this.validationErrors([{
        prop: this.$v.localEnergyPriceSet.description,
        rule: 'required',
        message: this.$t('required')
      }])
    },
    codeErrors () {
      return this.validationErrors([{
        prop: this.$v.localEnergyPriceSet.code,
        rule: 'required',
        message: this.$t('required')
      }])
    },
    typeErrors () {
      return this.validationErrors([{
        prop: this.$v.localEnergyPriceSet.type,
        rule: 'required',
        message: this.$t('required')
      }])
    },
    unitOfMeasureErrors () {
      return this.validationErrors([{
        prop: this.$v.localEnergyPriceSet.unit_of_measure,
        rule: 'required',
        message: this.$t('required')
      }])
    },
    currencyErrors () {
      return this.validationErrors([{
        prop: this.$v.localEnergyPriceSet.currency,
        rule: 'required',
        message: this.$t('required')
      }])
    }
  },
  data () {
    // Prepopulate our selected customers array if given an existing energy
    // price set with customers
    const selectedCustomers = this.energyPriceSet ? this.energyPriceSet.customers : []

    // If editing an existing record, create an edit clone to avoid modifying
    // elsewhere. cloneDeep seems to have issues with the output of
    // $orm('Model').createRecord, but we don't need to clone a new record, now
    // do we?
    const localEnergyPriceSet = cloneDeep(this.energyPriceSet) || this.$orm('EnergyPriceSet').createRecord({
      price_history: [],
      customers: []
    })

    return {
      isSaving: false,
      localEnergyPriceSet: localEnergyPriceSet,
      energyPriceSetTypes: map(EnergyPriceSetRecord.types, type => {
        return { text: this.$t(type), value: type }
      }),
      unitsOfMeasure: map(EnergyPriceSetRecord.units_of_measure, unit => {
        return { text: this.$t(unit), value: unit }
      }),
      currencies: map(EnergyPriceSetRecord.currencies, currency => {
        return { text: currency, value: currency }
      }),
      selectedCustomers: selectedCustomers,
      datePickerOptions: {
        format: this.$i18n.t('date_format_string'),
        locale: this.$i18n.locale
      },

      // Maintain price history in a seperate array in order to handle localized
      // dates
      localPriceHistory: this.priceHistoryWithLocalDates(localEnergyPriceSet.price_history)
    }
  },
  methods: {
    /**
     * Adds a new price object into the this.localPriceHistory array
     *
     * @return {undefined}
     */
    addPrice () {
      this.localPriceHistory.push({
        date: Moment(),
        price: this.localEnergyPriceSet.most_recent_price
      })
      this.sortLocalPriceHistory()
    },

    /**
     *
     */
    deletePrice (date) {
      this.localPriceHistory = filter(this.localPriceHistory, (priceHistoryItem) => {
        return priceHistoryItem.date.format() !== date.format()
      })
    },

    /**
     * Ensure all prices are shown in chronological order. Sorts
     * this.localPriceHistory based on the value of each data
     *
     * @return {undefined}
     */
    sortLocalPriceHistory () {
      this.localPriceHistory.sort((a, b) => {
        const aDate = (typeof a.date === 'string') ? Moment(a.date, this.datePickerOptions.format) : a.date
        const bDate = (typeof b.date === 'string') ? Moment(b.date, this.datePickerOptions.format) : b.date
        return (aDate - bDate)
      })
    },

    /**
     * Parses a priceHistory with ISO formatted dates to the format prefered by
     * the locale
     *
     * @param array<object>  priceHistory {date: 'YYYY-MM-DD', price: float}
     * @return array<object> in the form of {date: {localFormat}, price: float}
     */
    priceHistoryWithLocalDates (priceHistory) {
      return map(priceHistory, priceHistoryItem => {
        return {
          date: Moment(priceHistoryItem.date),
          price: priceHistoryItem.price
        }
      })
    },

    /**
     * Parses a priceHistory with locally formatted dates to YYYY-MM-DD
     *
     * @param array<object>  priceHistory {date: localFormat, price: float}
     * @return array<object> in the form of {date: 'YYYY-MM-DD', price: float}
     */
    priceHistoryWithIsoDates (priceHistory) {
      return map(priceHistory, priceHistoryItem => {
        return {
          date: priceHistoryItem.date.toISOString(),
          price: priceHistoryItem.price
        }
      })
    },

    /**
     * Validates the input and submits the EnergyPriceSet entity and makes any
     * additional API calls required to associated it with the customers
     * idenfitied
     *
     * @return {undefined}
     */
    onSubmit (e) {
      this.$v.$touch()
      if (this.$v.$invalid) return false

      this.isSaving = true
      this.localEnergyPriceSet.price_history = this.priceHistoryWithIsoDates(this.localPriceHistory)

      this.$orm('EnergyPriceSet').createOrUpdate(this.localEnergyPriceSet)
        .then(energyPriceSet => {
          const customerAssignmentRequests = []

          const customersToAssign = this.computeRecordsToAdd(get(this.localEnergyPriceSet, 'customers', []), this.selectedCustomers)
          forEach(customersToAssign, function (customer) {
            customerAssignmentRequests.push(energyPriceSet.assignCustomer(customer.id))
          })

          const customersToRemove = this.computeRecordsToRemove(get(this.localEnergyPriceSet, 'customers', []), this.selectedCustomers)
          forEach(customersToRemove, function (customer) {
            customerAssignmentRequests.push(energyPriceSet.removeCustomer(customer.id))
          })

          // Execute all the associations and then return the energy price set
          // to be emitted up to the parent component
          return Promise.all(customerAssignmentRequests)
            .then(() => {
              return Promise.resolve(energyPriceSet)
            })
        })
        .then((energyPriceSet) => {
          this.$emit('saved', energyPriceSet)
        })
        .catch(error => {
          this.handleError(error)
        })
        .finally(() => {
          this.isSaving = false
        })
    },

    /**
     * Filters the desired set of records against the records already assigned
     * (by record.id) and returns only those not yet assigned
     *
     * @return array
     */
    computeRecordsToAdd (originalRecords, newRecords) {
      return differenceWith(newRecords, originalRecords, (arrVal, othVal) => {
        return arrVal.id === othVal.id
      })
    },

    /**
     * Filters the desired set of records against the records already assigned
     * (by record.id) and returns only those which are currently assigned but
     * should not be
     *
     * @return array
     */
    computeRecordsToRemove (originalRecords, newRecords) {
      return differenceWith(originalRecords, newRecords, (arrVal, othVal) => {
        return arrVal.id === othVal.id
      })
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer
}
</style>
